/* eslint-disable @typescript-eslint/explicit-module-boundary-types */

import { Options } from "axios-hooks";
import formatUrl from "utils/formatUrl";

import api from "config/urls/api";

import {
  Product,
  ProductSetting,
  ProductStatus,
} from "./schema/productsSchema";
import { components, paths } from "./schema/publicSchema";
import { usePublicApi } from "./setup";
import { ApiError, PagedResponse } from "./shared";

export type ClientType =
  | "Unknown"
  | "ClientFreeTrial"
  | "ClientStartUp"
  | "ClientTestStartup"
  | "ClientTest"
  | "ClientProspect"
  | "ClientProd"
  | "CodatInternal"
  | "CodatSalesDemo"
  | "CodatQATest";

export type UserRole = "Administrator" | "Developer" | "Analyst" | "Onboarding";

export interface UserState {
  id: string;
  clientName: string;
  userName: string;
  portalLogo: string;
  userId: string;
  isPartner: boolean;
  userPermissions: string[];
  expiry?: string;
  companyLimit?: number;
  hourlySyncAllowed?: boolean;
  freeAccount?: boolean;
  dataIntegrityEnabled: boolean;
  clientType: ClientType;
  accountCategoriesEnabled: boolean;
  accountCategoriesV2Enabled: boolean;
  email: string;
  userRole: UserRole;
}

export const useGetUserStateQuery = () => {
  return usePublicApi<UserState, ApiError>({
    url: formatUrl(api.auth.userstate, {}),
  });
};

export const useContactSalesMutation = () => {
  return usePublicApi<void, ApiError>(
    {
      url: formatUrl(api.users.contactSales),
      method: "POST",
    },
    { manual: true }
  );
};

export type GetCompaniesParams =
  paths["/companies"]["get"]["parameters"]["query"];
export type GetCompaniesResponse =
  paths["/companies"]["get"]["responses"][200]["content"]["application/json"];
export type GetCompanyParams =
  paths["/companies/{companyId}"]["get"]["parameters"];
export type GetCompanyResponse =
  paths["/companies/{companyId}"]["get"]["responses"][200]["content"]["application/json"];
export type DataConnection =
  components["schemas"]["Codat.Public.Api.Models.Company.DataConnection"];

export const useGetCompaniesQuery = (
  {
    queryParams,
  }: {
    queryParams: GetCompaniesParams;
  },
  options?: Options
) => {
  return usePublicApi<GetCompaniesResponse, ApiError>(
    {
      url: formatUrl(api.companies.all, {}),
      params: queryParams,
    },
    options
  );
};

export type PostCompanyBody =
  paths["/companies"]["post"]["requestBody"]["content"]["application/json"];
export type PostCompanyResponse =
  paths["/companies"]["post"]["responses"][200]["content"]["application/json"];

export const useAddCompanyMutation = () => {
  return usePublicApi<PostCompanyResponse, ApiError, PostCompanyBody>(
    {
      url: formatUrl(api.companies.all, {}),
      method: "POST",
    },
    {
      manual: true,
    }
  );
};

export const useDeleteCompanyMutation = ({
  companyId,
}: {
  companyId: string;
}) => {
  return usePublicApi<void, ApiError>(
    {
      url: formatUrl(api.companies.one, { companyId }),
      method: "DELETE",
    },
    {
      manual: true,
    }
  );
};

export const useDeleteConnectionMutation = ({
  companyId,
  connectionId,
}: {
  companyId: string;
  connectionId: string;
}) => {
  return usePublicApi<void, ApiError>(
    {
      url: formatUrl(api.companies.connections.one, {
        companyId,
        connectionId,
      }),
      method: "DELETE",
    },
    {
      manual: true,
    }
  );
};

export const usePatchConnectionMutation = ({
  companyId,
  connectionId,
  status,
}: {
  companyId: string;
  connectionId: string;
  status: "Unlinked";
}) => {
  return usePublicApi<void, ApiError>(
    {
      url: formatUrl(api.companies.connections.one, {
        companyId,
        connectionId,
      }),
      method: "PATCH",
      data: { status },
    },
    {
      manual: true,
    }
  );
};

interface GetAlertsParams {
  page: number;
  pageSize: number;
}

export interface GetAlertsResponse {
  companyId: string;
  companyName?: string;
  clientId: string;
  clientName?: string;
  ruleId: string;
  ruleType: string;
  id: string;
  alertId: string;
  dataConnectionId?: string;
  message: string;
  data: string;
  raisedOnUtc: string;
}

export const useGetAlertsQuery = ({ ...queryParams }: GetAlertsParams) => {
  return usePublicApi<PagedResponse<GetAlertsResponse>, ApiError>(
    {
      url: formatUrl(api.rules.alerts),
      params: { ...queryParams },
    },
    {
      manual: false,
    }
  );
};

interface GetRulesParams {
  page: number;
  pageSize: number;
  orderBy: "type";
}

export interface AlertingRule {
  id: string;
  type: string;
  companyId?: string;
  notifiers: {
    emails: string[];
    webhook?: string;
  };
}

export const useGetRulesQuery = ({ ...queryParams }: GetRulesParams) => {
  return usePublicApi<PagedResponse<AlertingRule>, ApiError>(
    {
      url: formatUrl(api.rules.all),
      params: { ...queryParams },
    },
    {
      manual: false,
    }
  );
};

export const useDeleteRuleMutation = ({ ruleId }: { ruleId: string }) => {
  return usePublicApi<void, ApiError>(
    {
      url: formatUrl(api.rules.one, {
        ruleId,
      }),
      method: "DELETE",
    },
    {
      manual: true,
    }
  );
};

export const useEditRuleMutation = ({ ruleId }: { ruleId: string }) => {
  return usePublicApi<void, ApiError>(
    {
      url: formatUrl(api.rules.one, {
        ruleId,
      }),
      method: "PUT",
    },
    {
      manual: true,
    }
  );
};

export const useCreateRuleMutation = () => {
  return usePublicApi<void, ApiError>(
    {
      url: formatUrl(api.rules.all),
      method: "POST",
    },
    {
      manual: true,
    }
  );
};

export type GetClientProfileResponse =
  paths["/profile"]["get"]["responses"][200]["content"]["application/json"];

export const useGetClientProfileQuery = () => {
  return usePublicApi<GetClientProfileResponse, ApiError>({
    url: api.profile.one,
    method: "GET",
  });
};

export const useGetMaskedClientProfileQuery = () => {
  return usePublicApi<GetClientProfileResponse, ApiError>({
    url: api.profile.masked,
    method: "GET",
  });
};

export type PutClientProfileRequest =
  paths["/profile"]["put"]["requestBody"]["content"]["application/json"];

export type PutClientProfileResponse =
  paths["/profile"]["put"]["responses"][200]["content"]["application/json"];

export const usePutClientProfileMutation = () => {
  return usePublicApi<
    PutClientProfileResponse,
    ApiError,
    PutClientProfileRequest
  >(
    {
      url: api.profile.one,
      method: "PUT",
    },
    {
      manual: true,
    }
  );
};

interface AccountsCategoryLevels {
  levelName: string;
  confidence: number;
}
interface AccountsCategory {
  id: string;
  status: string;
  levels: AccountsCategoryLevels[];
}

export interface AccountsSummary {
  id: string;
  name: string;
  currency: string;
  category: AccountsCategory;
  nominalCode?: string;
  balance: number;
  priority: number;
}

export interface GetAccountsSummaryResponse {
  accounts: AccountsSummary[];
}

export const useGetAccountsSummary = (companyId?: string) => {
  return usePublicApi<GetAccountsSummaryResponse, ApiError>({
    url: formatUrl(api.companies.accountsSummary, { companyId }),
  });
};

export const useGetProductQuery = (productId: string) => {
  return usePublicApi<Product, ApiError>({
    url: formatUrl(api.products.one, { productId }),
    method: "GET",
  });
};

export const useGetProductsQuery = () => {
  return usePublicApi<Product[], ApiError>({
    url: formatUrl(api.products.all),
    method: "GET",
  });
};

interface UpdateProductStatusParams {
  productId: string;
  status: ProductStatus;
}

export const useUpdateProductStatus = ({
  productId,
  status,
}: UpdateProductStatusParams) => {
  return usePublicApi<unknown, ApiError>(
    {
      url: formatUrl(api.products.one, { productId }),
      method: "PUT",
      data: { status },
    },
    {
      manual: true,
    }
  );
};

export const useSetIsDefault = ({
  manual = true,
}: { manual?: boolean } = {}) => {
  return usePublicApi<undefined, ApiError>(
    {
      method: "PUT",
    },
    {
      manual,
    }
  );
};

export const useGetSettingDefinitions = (productKey: string) => {
  return usePublicApi<ProductSetting[], ApiError>(
    {
      url: formatUrl(api.products.settings.definitions, {
        productKey,
      }),
      method: "GET",
    },
    {
      manual: true,
    }
  );
};

export const useUpdateSettings = (productKey: string) => {
  return usePublicApi<
    Record<string, string | boolean | Date | null | undefined>,
    ApiError
  >(
    {
      url: formatUrl("/products/:productKey/settings", {
        productKey,
      }),
      method: "POST",
    },
    {
      manual: true,
    }
  );
};

export interface SyncOverview {
  syncId: string;
  clientId: string;
  companyName: string;
  companyId: string;
  dataConnectionId: string;
  sourceAccountId: string;
  executionStartUtc: string;
  executionEndUtc: string;
  periodStartUtc: string;
  periodEndUtc: string;
  status: string;
  errorMessage: string;
  errorCode: string;
  pushOperationKeys?: string[];
}

export interface GetPagedSyncOverviewsResponse {
  results: SyncOverview[];
  pages: {
    pageNumber: number;
    pageSize: number;
    totalResults: number;
    totalPages: number;
  };
  links: {
    next: string;
    previous: string;
  };
}

export const useGetPagedSyncOverviews = (
  clientId: string,
  pageNumber: number,
  pageSize: number,
  startDateTime: string,
  endDateTime: string,
  search: string
) => {
  let queryParams: Record<string, number | string> = {
    pageSize,
    pageNumber,
  };

  if (startDateTime) {
    queryParams = { ...queryParams, startDateTime };
  }

  if (endDateTime) {
    queryParams = { ...queryParams, endDateTime };
  }

  if (search) {
    queryParams = { ...queryParams, search };
  }

  return usePublicApi<GetPagedSyncOverviewsResponse, ApiError>({
    url: formatUrl(api.managedBankFeeds.syncs, { clientId }),
    params: queryParams,
    method: "GET",
  });
};

export const useGetSyncOverview = (clientId: string, syncId: string) => {
  const result = usePublicApi<SyncOverview, ApiError>({
    url: formatUrl(api.managedBankFeeds.sync, { clientId, syncId }),
    method: "GET",
  });
  return result;
};
